import { useMemo, useState } from "react";
import type { RowValue, SchemaColumn } from "../lib/api";
import { EMPTY_CELL, formatCell } from "../lib/format";
import { cx } from "../lib/cx";
import { Card } from "./Card";
import { ClayButton } from "./ClayButton";
import { ClayInput } from "./ClayInput";
import { ClaySelect } from "./ClaySelect";
import { EmptyState } from "./EmptyState";

export interface DataTableProps {
  columns: SchemaColumn[];
  rows: Array<Record<string, RowValue>>;
  fileName?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  pageSizeOptions?: number[];
}

type SortDirection = "asc" | "desc";

interface SortState {
  key: string;
  direction: SortDirection;
}

function isBlank(value: RowValue): boolean {
  return value === null || value === undefined || value === "";
}

function compareRows(left: RowValue, right: RowValue): number {
  if (isBlank(left) && isBlank(right)) {
    return 0;
  }
  if (isBlank(left)) {
    return 1;
  }
  if (isBlank(right)) {
    return -1;
  }
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  const leftNumber = typeof left === "string" ? Number(left) : Number.NaN;
  const rightNumber = typeof right === "string" ? Number(right) : Number.NaN;
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left).localeCompare(String(right), "id-ID", { numeric: true });
}

function csvCell(value: RowValue, column: SchemaColumn): string {
  const text = formatCell(value, column);
  return `"${(text === EMPTY_CELL ? "" : text).replace(/"/g, '""')}"`;
}

function downloadCsv(fileName: string, columns: SchemaColumn[], rows: Array<Record<string, RowValue>>): void {
  const header = columns.map((column) => `"${column.label.replace(/"/g, '""')}"`).join(";");
  const body = rows.map((row) =>
    columns.map((column) => csvCell(row[column.key], column)).join(";"),
  );
  const blob = new Blob([`\ufeff${[header, ...body].join("\r\n")}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName.replace(/[^\w.-]+/g, "-")}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function DataTable({
  columns,
  rows,
  fileName = "data",
  emptyTitle = "Belum ada data",
  emptyDescription = "Tidak ada baris yang cocok dengan filter saat ini.",
  pageSizeOptions = [10, 25, 50, 100],
}: DataTableProps) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0] ?? 10);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("id-ID");
    if (!needle) {
      return rows;
    }
    return rows.filter((row) =>
      columns.some((column) =>
        formatCell(row[column.key], column).toLocaleLowerCase("id-ID").includes(needle),
      ),
    );
  }, [rows, columns, query]);

  const sorted = useMemo(() => {
    if (!sort) {
      return filtered;
    }
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => compareRows(a[sort.key], b[sort.key]) * factor);
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const visibleRows = sorted.slice(startIndex, startIndex + pageSize);

  const toggleSort = (key: string) => {
    setPage(1);
    setSort((current) => {
      if (!current || current.key !== key) {
        return { key, direction: "asc" };
      }
      if (current.direction === "asc") {
        return { key, direction: "desc" };
      }
      return null;
    });
  };

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 p-5">
        <div className="w-full max-w-xs">
          <ClayInput
            label="Cari data"
            hideLabel
            placeholder="Cari di semua kolom…"
            type="search"
            value={query}
            onValueChange={(value) => {
              setQuery(value);
              setPage(1);
            }}
          />
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted">
            {sorted.length.toLocaleString("id-ID")} baris
          </p>
          <ClayButton
            variant="secondary"
            size="sm"
            disabled={sorted.length === 0}
            onClick={() => downloadCsv(fileName, columns, sorted)}
            leftIcon={
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3v12M7.5 10.5 12 15l4.5-4.5M4 20h16" />
              </svg>
            }
          >
            Ekspor CSV
          </ClayButton>
        </div>
      </div>

      {sorted.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="bg-bg/60 text-left">
                {columns.map((column) => {
                  const active = sort?.key === column.key;
                  return (
                    <th key={column.key} scope="col" className="whitespace-nowrap px-5 py-3">
                      <button
                        type="button"
                        onClick={() => toggleSort(column.key)}
                        className={cx(
                          "inline-flex items-center gap-1.5 font-semibold transition-colors",
                          active ? "text-accent" : "text-muted hover:text-text",
                          column.type === "number" && "flex-row-reverse",
                        )}
                      >
                        <span>{column.label}</span>
                        <span aria-hidden="true" className="text-xs">
                          {active ? (sort.direction === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, rowIndex) => (
                <tr
                  key={startIndex + rowIndex}
                  className="align-top transition-colors hover:bg-bg/50"
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cx(
                        "whitespace-nowrap px-5 py-3 text-text",
                        column.type === "number" && "text-right tabular-nums",
                      )}
                    >
                      {formatCell(row[column.key], column)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sorted.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <div className="flex items-center gap-3 text-sm text-muted">
            <span>
              Menampilkan {startIndex + 1}–{Math.min(startIndex + pageSize, sorted.length)} dari{" "}
              {sorted.length.toLocaleString("id-ID")}
            </span>
            <div className="w-28">
              <ClaySelect
                label="Baris per halaman"
                hideLabel
                value={String(pageSize)}
                onValueChange={(value) => {
                  setPageSize(Number(value));
                  setPage(1);
                }}
                options={pageSizeOptions.map((size) => ({
                  value: String(size),
                  label: `${size} / hal.`,
                }))}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ClayButton
              variant="secondary"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
            >
              Sebelumnya
            </ClayButton>
            <span className="text-sm text-muted">
              {currentPage} / {totalPages}
            </span>
            <ClayButton
              variant="secondary"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(currentPage + 1)}
            >
              Berikutnya
            </ClayButton>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
